import { ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  quickRepliesFromMessage,
  type ActiveQuickReplies,
  type QuickReplyOption,
  type QuickReplyQuestion,
} from '@/components/case-partner/quick-replies';
import { cn } from '@/lib/utils';

interface QuickRepliesPanelProps {
  className?: string;
  disabled?: boolean;
  onSubmit: (value: string, answers: QuickReplyAnswer[]) => void;
  questions: QuickReplyQuestion[];
  reserveSpace?: boolean;
}

export interface QuickReplyAnswer {
  question: string;
  answer: string;
}

interface QuickReplyButtonProps {
  isSelected?: boolean;
  optionIndex: number;
  option: QuickReplyOption;
  disabled: boolean;
  onSelect: (value: string) => void;
}

export { quickRepliesFromMessage };

const customAnswerForQuestion = (
  question: QuickReplyQuestion | undefined,
  answer: string | undefined,
) => {
  if (!question || !answer) return '';
  return question.options.some((option) => option.value === answer) ? '' : answer;
};

export const getActiveQuickReplies = (
  messages: Array<{ role: string; message_metadata?: unknown }>,
): ActiveQuickReplies | null => {
  const lastUserIndex = messages.reduce(
    (latestIndex, message, index) => (message.role === 'user' ? index : latestIndex),
    -1,
  );

  for (let index = messages.length - 1; index > lastUserIndex; index -= 1) {
    const message = messages[index];
    if (message.role !== 'assistant') continue;

    const quickReplies = quickRepliesFromMessage(message);
    if (quickReplies) return quickReplies;
  }

  return null;
};

const QuickReplyButton: React.FC<QuickReplyButtonProps> = memo(({
  isSelected = false,
  optionIndex,
  option,
  disabled,
  onSelect,
}) => {
  const handleClick = useCallback(() => {
    onSelect(option.value);
  }, [onSelect, option.value]);

  return (
    <button
      type="button"
      className={cn(
        'group inline-grid w-full max-w-full grid-cols-[1.35rem_minmax(0,1fr)] items-center gap-2 rounded-md px-1.5 py-2 text-left text-[13px] font-medium leading-5 text-slate-700',
        'transition-all hover:translate-x-0.5 hover:bg-[#eef3fb]/70 hover:text-slate-950',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:text-slate-700',
        isSelected && 'bg-[#eef3ff]/70 text-[#26345d] hover:text-[#26345d]',
      )}
      onClick={handleClick}
      disabled={disabled}
      title={option.label}
    >
      <span className={cn(
        'flex h-5 w-5 items-center justify-center rounded-md bg-[#eef2f7] text-[11px] font-semibold text-[#7a88a6] ring-1 ring-[#dbe3ef] transition-colors',
        'group-hover:bg-[#e4ebf5] group-hover:text-[#4d5e82]',
        isSelected && 'bg-[#6475a6] text-white group-hover:bg-[#596b9e] group-hover:text-white',
      )}>
        {optionIndex}
      </span>
      <span className="line-clamp-2 min-w-0 break-words">{option.label}</span>
    </button>
  );
});
QuickReplyButton.displayName = 'QuickReplyButton';

export const QuickRepliesPanel: React.FC<QuickRepliesPanelProps> = memo(({
  className,
  disabled = false,
  onSubmit,
  questions,
  reserveSpace = false,
}) => {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [customAnswer, setCustomAnswer] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const customInputRef = useRef<HTMLInputElement | null>(null);
  const hasSubmittedRef = useRef(false);
  const questionsSignature = useMemo(() => (
    questions
      .map((question) => [
        question.question,
        question.placeholder || '',
        question.options.map((option) => `${option.label}:${option.value}`).join('|'),
      ].join(':'))
      .join('||')
  ), [questions]);
  const previousQuestionsSignatureRef = useRef(questionsSignature);

  useEffect(() => {
    if (previousQuestionsSignatureRef.current === questionsSignature) return;

    previousQuestionsSignatureRef.current = questionsSignature;
    setAnswers({});
    setCurrentIndex(0);
    setCustomAnswer('');
    setHasSubmitted(false);
    hasSubmittedRef.current = false;
  }, [questionsSignature]);

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentIndex];
  const selectedCustomAnswer = customAnswerForQuestion(currentQuestion, currentAnswer);
  const isCustomAnswerSelected = Boolean(
    selectedCustomAnswer && customAnswer === selectedCustomAnswer,
  );
  const isLastQuestion = currentIndex === questions.length - 1;
  const isDisabled = disabled || hasSubmitted;
  const customAnswerPlaceholder = currentQuestion?.placeholder
    || 'Type your own answer...';
  const reservedMinHeight = useMemo(() => {
    const maxChoices = questions.reduce(
      (max, question) => Math.max(max, question.options.length + 1),
      0,
    );
    const estimatedOptionRows = Math.min(3, Math.max(1, Math.ceil(maxChoices / 3)));

    return 22 + (estimatedOptionRows * 38);
  }, [questions]);

  const buildResponse = useCallback((nextAnswers: Record<number, string>) => (
    questions
      .map((question, index) => `${question.question}: ${nextAnswers[index]}`)
      .join('\n')
  ), [questions]);

  const buildAnswerSummary = useCallback((nextAnswers: Record<number, string>) => (
    questions
      .map((question, index): QuickReplyAnswer | null => {
        const answer = nextAnswers[index];
        return answer ? { question: question.question, answer } : null;
      })
      .filter((answer): answer is QuickReplyAnswer => Boolean(answer))
  ), [questions]);

  const answersWithUpdate = useCallback((index: number, value: string) => {
    if (answers[index] === value) return answers;

    const nextAnswers: Record<number, string> = {};

    for (let answerIndex = 0; answerIndex < index; answerIndex += 1) {
      const existingAnswer = answers[answerIndex];
      if (existingAnswer) {
        nextAnswers[answerIndex] = existingAnswer;
      }
    }

    nextAnswers[index] = value;
    return nextAnswers;
  }, [answers]);

  const submitAnswers = useCallback((nextAnswers: Record<number, string>) => {
    if (disabled || hasSubmittedRef.current) return;

    hasSubmittedRef.current = true;
    setHasSubmitted(true);
    onSubmit(buildResponse(nextAnswers), buildAnswerSummary(nextAnswers));
  }, [buildAnswerSummary, buildResponse, disabled, onSubmit]);

  const moveToNextQuestion = useCallback(() => {
    if (!isLastQuestion) {
      setCurrentIndex((index) => index + 1);
    }
  }, [isLastQuestion]);

  const handleSelect = useCallback((value: string) => {
    if (isDisabled) return;

    const nextAnswers = answersWithUpdate(currentIndex, value);

    setAnswers(nextAnswers);
    setCustomAnswer('');

    if (isLastQuestion) {
      submitAnswers(nextAnswers);
      return;
    }

    moveToNextQuestion();
  }, [answersWithUpdate, currentIndex, isDisabled, isLastQuestion, moveToNextQuestion, submitAnswers]);

  const handleBack = useCallback(() => {
    if (isDisabled) return;

    const nextIndex = Math.max(currentIndex - 1, 0);
    const restoredCustomAnswer = customAnswerForQuestion(
      questions[nextIndex],
      answers[nextIndex],
    );

    setCurrentIndex(nextIndex);
    setCustomAnswer(restoredCustomAnswer);
  }, [answers, currentIndex, isDisabled, questions]);

  const canNavigateNext = currentIndex < questions.length - 1
    && Boolean(currentAnswer);

  const handleNext = useCallback(() => {
    if (isDisabled || !canNavigateNext) return;

    const nextIndex = currentIndex + 1;
    const restoredCustomAnswer = customAnswerForQuestion(
      questions[nextIndex],
      answers[nextIndex],
    );

    setCurrentIndex(nextIndex);
    setCustomAnswer(restoredCustomAnswer);
  }, [answers, canNavigateNext, currentIndex, isDisabled, questions]);

  const handleUseCustomAnswer = useCallback(() => {
    if (isDisabled) return;

    const trimmedAnswer = customAnswer.trim();
    if (!trimmedAnswer) return;

    const nextAnswers = answersWithUpdate(currentIndex, trimmedAnswer);

    setAnswers(nextAnswers);
    setCustomAnswer('');

    if (isLastQuestion) {
      submitAnswers(nextAnswers);
      return;
    }

    moveToNextQuestion();
  }, [answersWithUpdate, currentIndex, customAnswer, isDisabled, isLastQuestion, moveToNextQuestion, submitAnswers]);

  const handleCustomAnswerChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomAnswer(event.target.value);
  }, []);

  const handleCustomAnswerKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    handleUseCustomAnswer();
  }, [handleUseCustomAnswer]);

  const stopCustomInputClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }, []);

  const handleCustomAnswerButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleUseCustomAnswer();
  }, [handleUseCustomAnswer]);

  const focusCustomInput = useCallback(() => {
    customInputRef.current?.focus();
  }, []);

  if (!currentQuestion) return null;

  return (
    <div className={cn('w-full max-w-full', className)}>
      <div className="relative w-full overflow-hidden rounded-xl border border-[#dfe6f5] bg-[linear-gradient(180deg,#fbfdff_0%,#f7f9fd_100%)] px-4 py-3 shadow-[0_14px_36px_rgba(38,52,93,0.08)]">
        <div
          className="flex w-full flex-col justify-end gap-2.5"
          style={reserveSpace ? { minHeight: reservedMinHeight } : undefined}
        >
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 text-[12px] font-semibold leading-4 text-[#52607a]">
              {currentQuestion.question}
            </div>
            {questions.length > 1 && (
              <div className="ml-2 flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className="inline-flex h-6 shrink-0 items-center gap-0.5 rounded-full bg-white/65 px-2 text-[11px] font-medium leading-4 text-slate-500 ring-1 ring-slate-200/80 transition-all hover:bg-white hover:text-slate-800 hover:shadow-[0_4px_14px_rgba(15,23,42,0.06)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/65 disabled:hover:text-slate-500 disabled:hover:shadow-none"
                  disabled={isDisabled || currentIndex === 0}
                  onClick={handleBack}
                >
                  <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  aria-label="Next question"
                  className="inline-flex h-6 shrink-0 items-center gap-0.5 rounded-full bg-white/65 px-2 text-[11px] font-medium leading-4 text-slate-500 ring-1 ring-slate-200/80 transition-all hover:bg-white hover:text-slate-800 hover:shadow-[0_4px_14px_rgba(15,23,42,0.06)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/65 disabled:hover:text-slate-500 disabled:hover:shadow-none"
                  disabled={isDisabled || !canNavigateNext}
                  onClick={handleNext}
                >
                  <span>Next</span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                </button>
              </div>
            )}
            {questions.length > 1 && (
              <div className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium leading-4 text-slate-400 ring-1 ring-slate-200/80">
                {currentIndex + 1} / {questions.length}
              </div>
            )}
          </div>
          <div className="flex w-full flex-col items-stretch divide-y divide-[#dfe6f2]/80">
            {currentQuestion.options.map((option, optionIndex) => (
              <QuickReplyButton
                key={option.value}
                disabled={isDisabled}
                isSelected={currentAnswer === option.value}
                onSelect={handleSelect}
                optionIndex={optionIndex + 1}
                option={option}
              />
            ))}
            <div
              className={cn(
                'grid w-full max-w-full grid-cols-[1.35rem_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1.5 py-2 transition-colors focus-within:bg-[#eef3fb]/55',
                isCustomAnswerSelected && 'bg-[#eef3ff]/70 text-[#26345d]',
              )}
              onClick={focusCustomInput}
            >
              <span className={cn(
                'flex h-5 w-5 items-center justify-center rounded-md bg-[#eef2f7] text-[11px] font-semibold text-[#7a88a6] ring-1 ring-[#dbe3ef] transition-colors',
                isCustomAnswerSelected && 'bg-[#6475a6] text-white',
              )}>
                {currentQuestion.options.length + 1}
              </span>
              <input
                ref={customInputRef}
                className={cn(
                  'h-6 min-w-0 bg-transparent text-[13px] font-medium leading-5 text-slate-800 outline-hidden placeholder:text-slate-400',
                  isCustomAnswerSelected && 'text-[#26345d]',
                )}
                disabled={isDisabled}
                onChange={handleCustomAnswerChange}
                onKeyDown={handleCustomAnswerKeyDown}
                onClick={stopCustomInputClick}
                placeholder={customAnswerPlaceholder}
                value={customAnswer}
              />
              <button
                type="button"
                className={cn(
                  'inline-flex h-7 shrink-0 items-center rounded-md bg-white/80 px-2 text-xs font-medium leading-5 text-slate-600 ring-1 ring-slate-200/80',
                  'transition-all hover:text-slate-950 hover:ring-[#b9c6e7]',
                  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-slate-600 disabled:hover:ring-slate-200/80',
                )}
                disabled={isDisabled || !customAnswer.trim()}
                onClick={handleCustomAnswerButtonClick}
              >
                {isLastQuestion ? 'Send' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
QuickRepliesPanel.displayName = 'QuickRepliesPanel';
